import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Users, Mail, Crown, Shield, User, Trash2, Copy, Check, Loader2, Plus
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_ICON = { admin: Crown, manager: Shield, member: User };

/**
 * Members + invitations management dialog for a workspace.
 *
 * Permission model (enforced by backend, mirrored here for UX):
 *   - admin or manager: see/send/revoke invitations
 *   - member: see member list only
 */
export default function MembersDialog({ open, onOpenChange, workspace, token, currentUserId }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [sending, setSending] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  // Find the current user's role in this workspace to decide what actions they see.
  const currentRole = members.find((m) => m.user_id === currentUserId)?.role || 'member';
  const canManage = currentRole === 'admin' || currentRole === 'manager';

  const fetchAll = useCallback(async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/workspaces/${workspace.id}/members`, axiosConfig),
        // Invitations endpoint 403s for non-managers — swallow so members still see member list.
        axios.get(`${API}/workspaces/${workspace.id}/invitations`, axiosConfig).catch(() => ({ data: [] }))
      ]);
      setMembers(membersRes.data);
      setInvitations(invitesRes.data);
    } catch (error) {
      toast.error(t('members.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, token, t]);

  useEffect(() => {
    if (open && workspace) fetchAll();
  }, [open, workspace, fetchAll]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/workspaces/${workspace.id}/invitations`,
        { workspace_id: workspace.id, email: inviteForm.email.trim(), role: inviteForm.role },
        axiosConfig
      );
      toast.success(t('members.invite.sent'));
      setInviteForm({ email: '', role: 'member' });
      // Auto-copy the deep link so the inviter can share it directly even if email failed.
      const link = `${window.location.origin}/invite/${data.token}`;
      try { await navigator.clipboard.writeText(link); toast.info(t('members.invite.linkCopied')); } catch (_) {}
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('members.invite.error'));
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (invitationId) => {
    if (!window.confirm(t('members.invite.revokeConfirm'))) return;
    try {
      await axios.delete(`${API}/invitations/${invitationId}`, axiosConfig);
      toast.success(t('members.invite.revoked'));
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('members.invite.revokeError'));
    }
  };

  const copyInviteLink = async (inviteToken) => {
    const link = `${window.location.origin}/invite/${inviteToken}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(inviteToken);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (_) {
      toast.error(t('members.invite.copyFail'));
    }
  };

  const pendingInvites = invitations.filter((i) => i.status === 'pending');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="members-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            {workspace?.name} · {t('members.title')}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('members.loading')}
          </div>
        ) : (
          <div className="space-y-6 mt-2">
            {/* Invite form — only for managers/admins */}
            {canManage && (
              <section>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{t('members.invite.title')}</h3>
                <form onSubmit={sendInvite} className="flex flex-col sm:flex-row gap-2" data-testid="invite-form">
                  <Input
                    type="email"
                    placeholder={t('members.invite.emailPlaceholder')}
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    className="flex-1 text-sm"
                    data-testid="invite-email-input"
                  />
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
                    data-testid="invite-role-select"
                  >
                    <option value="member">{t('members.role.member')}</option>
                    <option value="manager">{t('members.role.manager')}</option>
                    <option value="admin">{t('members.role.admin')}</option>
                  </select>
                  <Button type="submit" disabled={sending || !inviteForm.email.trim()} className="rounded-full" data-testid="invite-submit-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 sm:mr-1.5" />}
                    <span className="hidden sm:inline">{t('members.invite.send')}</span>
                  </Button>
                </form>
                <p className="text-[10px] text-muted-foreground mt-1.5 ml-1">{t('members.invite.linkHint')}</p>
              </section>
            )}

            {/* Pending invitations */}
            {canManage && pendingInvites.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{t('members.pending.title', { count: pendingInvites.length })}</h3>
                <div className="space-y-2">
                  {pendingInvites.map((inv) => {
                    const RoleIcon = ROLE_ICON[inv.role] || User;
                    const isCopied = copiedToken === inv.token;
                    return (
                      <div key={inv.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card/30">
                        <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{inv.email}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <RoleIcon className="w-2.5 h-2.5" /> {t(`members.role.${inv.role}`)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyInviteLink(inv.token)}
                          className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md hover:bg-secondary/60"
                          title={t('members.invite.copyLink')}
                          data-testid={`copy-invite-${inv.id}`}
                        >
                          {isCopied ? <Check className="w-3.5 h-3.5 text-accent" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => revokeInvite(inv.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-md hover:bg-destructive/10"
                          title={t('members.invite.revoke')}
                          data-testid={`revoke-invite-${inv.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Member list */}
            <section>
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                {t('members.list.title', { count: members.length })}
              </h3>
              <div className="space-y-2">
                {members.map((m) => {
                  const RoleIcon = ROLE_ICON[m.role] || User;
                  const isMe = m.user_id === currentUserId;
                  return (
                    <div key={m.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card/30">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium">
                          {(m.name || m.email || '?').slice(0, 1).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {m.name || m.email}
                          {isMe && <span className="text-xs text-muted-foreground ml-2">({t('members.list.you')})</span>}
                        </p>
                        {m.name && m.email && (
                          <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                        )}
                      </div>
                      <span className="text-xs px-2 py-1 bg-secondary/60 rounded-full flex items-center gap-1 flex-shrink-0">
                        <RoleIcon className="w-3 h-3" />
                        {t(`members.role.${m.role}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
